import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Inbox, ArrowRight } from "lucide-react";
import { mailboxApi } from "@/lib/api";

const SERIES = [
  { key: "support", label: "Support", match: /support/i, color: "#E8700A" },
  { key: "updates", label: "Updates", match: /update|release/i, color: "#1E3A5F" },
  { key: "invoices", label: "Rechnungen", match: /rechnung/i, color: "#16A34A" },
];
const fmtWeek = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });

const toRows = ({ weeks, topics }) => {
  const ids = Object.fromEntries(SERIES.map((s) => [s.key, topics.filter((t) => s.match.test(t.name)).map((t) => t.id)]));
  return weeks.map((w) => {
    const row = { week: `KW ab ${fmtWeek(w.week_start)}` };
    SERIES.forEach((s) => { row[s.key] = ids[s.key].reduce((n, id) => n + (w.counts[id] || 0), 0); });
    return row;
  });
};

export function MailTopicsWeeklyCard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { mailboxApi.weeklyStats(8).then((r) => setRows(toRows(r.data))).catch(() => setRows([])); }, []);
  const totals = SERIES.map((s) => ({ ...s, sum: (rows || []).reduce((n, r) => n + r[s.key], 0) }));

  return (
    <Card data-testid="dashboard-mail-topics-widget">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Inbox className="w-4 h-4 text-primary" />Service-Postfach nach Thema</CardTitle>
          <CardDescription>Eingegangene Mails pro Woche · letzte 8 Wochen</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild><Link to="/mailbox" data-testid="dashboard-mail-topics-link">Postfach<ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-4 mb-3">
          {totals.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-sm" data-testid={`mail-topics-total-${s.key}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-semibold tabular-nums">{rows ? s.sum : "…"}</span>
            </div>
          ))}
        </div>
        <div className="h-64" data-testid="mail-topics-chart">
          {rows && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {SERIES.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[3, 3, 0, 0]} maxBarSize={22} />)}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
